import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { Cancel as CancelIcon } from "@material-ui/icons";
import { useState, useEffect } from "react";
import {
  useBulkRechazarMutation,
  useRechazarMovimientoMutation,
} from "src/services/conciliacionApi";
import { formatearMonto } from "src/utils/conciliacionHelpers";
import { useSnackbar } from "src/components/snackbar";
import { formatearFechaDisplay } from "src/utils/formatTime";

export default function DialogRechazarMovimientos({
  open,
  onClose,
  movimientos = [], // Array de movimientos (para bulk) o array con 1 elemento
}) {
  const [motivo, setMotivo] = useState("");

  const [rechazarMovimiento, { isLoading: isRechazandoIndividual }] =
    useRechazarMovimientoMutation();

  const [rechazarBulk, { isLoading: isRechazandoBulk }] =
    useBulkRechazarMutation();

  const isRechazando = isRechazandoIndividual || isRechazandoBulk;

  const { enqueueSnackbar } = useSnackbar();

  // Limpiar motivo al abrir/cerrar el diálogo
  useEffect(() => {
    if (open) {
      setMotivo("");
    }
  }, [open]);

  const truncarTexto = (texto, maxLength = 50) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  const esBulk = movimientos.length > 1;

  const handleClose = () => {
    if (!isRechazando) {
      setMotivo("");
      onClose();
    }
  };

  const handleMotivoChange = (event) => {
    setMotivo(event.target.value);
  };

  const handleConfirmarRechazo = async () => {
    if (movimientos.length === 0) return;

    try {
      if (esBulk) {
        // Rechazar en lote
        const resultado = await rechazarBulk({
          movimiento_ids: movimientos.map((m) => m.id),
          motivo: motivo || "Rechazado en lote",
        }).unwrap();

        const { exitosos, fallidos } = resultado.resultados;

        if (fallidos.length > 0) {
          enqueueSnackbar(
            `${exitosos.length} rechazados, ${fallidos.length} fallaron`,
            { variant: "warning" },
          );
        } else {
          enqueueSnackbar(
            `${exitosos.length} movimientos rechazados exitosamente`,
            { variant: "success" },
          );
        }
      } else {
        // Rechazar individual
        await rechazarMovimiento({
          movimiento_id: movimientos[0].id,
          motivo: motivo || "Rechazado manualmente",
        }).unwrap();

        enqueueSnackbar("Movimiento rechazado exitosamente", {
          variant: "success",
        });
      }

      // Limpiar y cerrar
      setMotivo("");
      onClose();
    } catch (error) {
      console.error("Error al rechazar movimiento(s):", error);
      enqueueSnackbar(
        error?.data?.message || "Error al rechazar el/los movimiento(s)",
        { variant: "error" },
      );
    }
  };

  // ==================== VALIDACIONES ====================

  if (!movimientos || movimientos.length === 0) {
    return null;
  }

  // ==================== RENDER ====================

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" component="div" style={{ color: "#f44336" }}>
            ¿Confirmar Rechazo de {esBulk ? "Movimientos" : "Movimiento"}?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          {esBulk
            ? `Se marcarán ${movimientos.length} movimientos como IGNORADOS. Esta acción puede deshacerse posteriormente.`
            : "El movimiento será marcado como IGNORADO. Esta acción puede deshacerse posteriormente."}
        </DialogContentText>

        {/* INFORMACIÓN DEL/LOS MOVIMIENTO(S) */}
        <Box mt={2} mb={2}>
          {esBulk ? (
            <Card variant="outlined" style={{ backgroundColor: "#FFF3E0" }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  Movimientos a rechazar:
                </Typography>
                <List dense>
                  {movimientos.slice(0, 5).map((mov) => (
                    <ListItem key={mov.id}>
                      <ListItemText
                        primary={`${formatearMonto(mov.monto)} - ${truncarTexto(
                          mov.glosa,
                          40,
                        )}`}
                        secondary={`Fecha: ${formatearFechaDisplay(
                          mov.fecha_mov,
                        )}`}
                      />
                    </ListItem>
                  ))}
                  {movimientos.length > 5 && (
                    <ListItem>
                      <ListItemText
                        primary={`... y ${movimientos.length - 5} más`}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" style={{ backgroundColor: "#FFF3E0" }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  Movimiento:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatearMonto(movimientos[0].monto)} -{" "}
                  {truncarTexto(movimientos[0].glosa, 50)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  style={{ marginTop: 8 }}
                >
                  Fecha: {formatearFechaDisplay(movimientos[0].fecha_mov)}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* CAMPO DE MOTIVO */}
        <TextField
          autoFocus
          margin="dense"
          label="Motivo del rechazo (opcional)"
          fullWidth
          multiline
          rows={3}
          value={motivo}
          onChange={handleMotivoChange}
          placeholder="Ej: Movimiento duplicado, error en la glosa..."
          variant="outlined"
          helperText="Este motivo quedará registrado en la auditoría"
          disabled={isRechazando}
        />
      </DialogContent>

      {/* ACCIONES */}
      <DialogActions>
        <Button onClick={handleClose} disabled={isRechazando}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmarRechazo}
          color="secondary"
          variant="contained"
          disabled={isRechazando}
          startIcon={
            isRechazando ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CancelIcon />
            )
          }
        >
          {isRechazando ? "Rechazando..." : "Confirmar Rechazo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
